import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Search, ChevronRight, AlertTriangle, Sparkles, Settings, Download, Filter, RefreshCw, FileText,
} from "lucide-react";
import { resolveTollStatus } from "@/lib/statusResolvers";
import { usePermission } from "@/lib/usePermission";
import TollTransactionDetailModal from "@/components/TollTransactionDetailModal";
import TollBillDetailModal from "@/components/TollBillDetailModal";
import TollGenerateInvoicesModal from "@/components/TollGenerateInvoicesModal";

const STATUS_FILTERS = [
    { value: "",         label: "All" },
    { value: "matched",  label: "Matched" },
    { value: "unmatched",label: "Unmatched" },
    { value: "disputed", label: "Disputed" },
    { value: "invoiced", label: "Invoiced" },
    { value: "void",     label: "Void" },
    { value: "new",      label: "New" },
];

const DISPUTE_FILTERS = [
    { value: "", label: "All" },
    { value: "true", label: "Disputed only" },
    { value: "false", label: "Non-disputed" },
];

const LIMIT_OPTIONS = [25, 50, 100];

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(s) { if (!s) return "—"; return String(s).slice(0, 10); }

/**
 * Phase TOLLBILL-P3a (2026-07-10) — Admin Tolls page.
 *
 * Wires the Phase-2 backend surface into the admin UI:
 *   - GET  /admin/tolls/transactions           — filtered list
 *   - GET  /admin/tolls/settings               — global markup (link out to settings page)
 *   - POST /admin/tolls/invoices/generate      — manual invoice run modal
 *   - POST .../mark-disputed | resolve-dispute — via the detail modal
 *
 * All URL state → search params so the view is shareable + refreshable.
 */
export default function AdminTollsListPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const status = searchParams.get("status") || "";
    const disputeFilter = searchParams.get("dispute") || "";
    const dateFrom = searchParams.get("from") || "";
    const dateTo = searchParams.get("to") || "";
    const customerId = searchParams.get("customer_id") || "";
    const rentalId = searchParams.get("rental_id") || "";
    const limit = Number(searchParams.get("limit") || 50);
    const skip = Number(searchParams.get("skip") || 0);
    const [q, setQ] = useState(searchParams.get("q") || "");

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const [detailTxnId, setDetailTxnId] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [billDetailId, setBillDetailId] = useState(null);
    const [billDetailOpen, setBillDetailOpen] = useState(false);
    const [genOpen, setGenOpen] = useState(false);

    const { ready: permReady, hasPermission } = usePermission();
    const canView = hasPermission("Tolls", "View");
    const canManage = hasPermission("Tolls", "Manage");
    const canInvoice = hasPermission("Tolls", "Invoice");

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === "" || value == null) next.delete(key);
        else next.set(key, String(value));
        // Reset paging whenever a filter changes
        if (key !== "skip" && key !== "limit") next.delete("skip");
        setSearchParams(next, { replace: true });
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit, skip };
            if (status) params.status = status;
            if (disputeFilter !== "") params.dispute_flag = disputeFilter === "true";
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            // Phase TOLLBILL-P4c (2026-07-10) — Customer/Rental filter
            // inputs accept BOTH a UUID and a free-text name/unit.
            // Detect UUID-ish shape (36 chars w/ hyphens); anything else
            // is folded into the `q` param so the server-side join
            // resolves it via customers.company_name / rentals.unit_number.
            const _isUuid = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
            const _qExtras = [];
            if (customerId) {
                if (_isUuid(customerId)) params.customer_id = customerId;
                else _qExtras.push(customerId.trim());
            }
            if (rentalId) {
                if (_isUuid(rentalId)) params.rental_id = rentalId;
                else _qExtras.push(rentalId.trim());
            }
            const qStr = [(searchParams.get("q") || "").trim(), ..._qExtras]
                .filter(Boolean).join(" ").trim();
            if (qStr) params.q = qStr;
            const { data } = await api.get("/admin/tolls/transactions", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [status, disputeFilter, dateFrom, dateTo, customerId, rentalId, limit, skip, searchParams.get("q")]);

    const onSearch = (e) => {
        e.preventDefault();
        updateParam("q", q.trim());
    };

    const openDetail = (txn) => {
        setDetailTxnId(txn.transaction_id);
        setDetailOpen(true);
    };

    // Phase TOLLBILL-P5a+ — transaction-level dispute endpoints return
    // HTTP 410; dispute is now a bill-level operation. Users open the
    // parent bill (via the Parent bill column) to manage disputes.

    const exportCsv = () => {
        // Frontend-only CSV of the current page. No new backend surface.
        if (!items.length) { toast.info("Nothing to export"); return; }
        const cols = ["transaction_id","posted_at","plate","location","amount_cents","currency","customer_id","rental_id","status","dispute_flag","invoice_id"];
        const lines = [cols.join(",")];
        for (const t of items) {
            const row = cols.map((c) => {
                const raw = t[c];
                if (raw == null) return "";
                const s = String(raw).replace(/"/g, '""');
                return `"${s}"`;
            });
            lines.push(row.join(","));
        }
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tolls-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / (limit || 1))), [total, limit]);
    const currentPage = useMemo(() => Math.floor(skip / (limit || 1)) + 1, [skip, limit]);

    // Phase TOLLBILL-P4b — client-side id → label caches for the admin
    // page's own table (mirrors the shared TollTransactionsList pattern).
    const [customerNames, setCustomerNames] = useState({});
    const [rentalUnits, setRentalUnits] = useState({});
    useEffect(() => {
        if (!items.length) return;
        const uniqCust = Array.from(new Set(items.map((t) => t.customer_id).filter(Boolean)))
            .filter((id) => !(id in customerNames));
        const uniqRent = Array.from(new Set(items.map((t) => t.rental_id).filter(Boolean)))
            .filter((id) => !(id in rentalUnits));
        uniqCust.forEach((id) => {
            api.get(`/admin/customers/${encodeURIComponent(id)}`)
                .then(({ data }) => setCustomerNames((p) => ({ ...p, [id]: data?.company_name || null })))
                .catch(() => setCustomerNames((p) => ({ ...p, [id]: null })));
        });
        uniqRent.forEach((id) => {
            api.get(`/admin/rentals/${encodeURIComponent(id)}`)
                .then(({ data }) => setRentalUnits((p) => ({ ...p, [id]: data?.unit_number || null })))
                .catch(() => setRentalUnits((p) => ({ ...p, [id]: null })));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);
    const customerLabel = (id) => id ? (id in customerNames ? (customerNames[id] || `${id.slice(0,8)}…`) : `${id.slice(0,8)}…`) : null;
    const rentalLabel = (id) => id ? (id in rentalUnits ? (rentalUnits[id] || `${id.slice(0,8)}…`) : `${id.slice(0,8)}…`) : null;

    // Permission gate: super_admin bypass is baked into usePermission
    if (permReady && !canView) {
        return (
            <DashboardShell>
                <Card className="mt-8">
                    <CardContent className="p-10 text-center">
                        <AlertTriangle size={28} className="mx-auto text-amber-500" />
                        <h2 className="mt-3 font-display text-xl font-semibold" data-testid="tolls-access-denied">Access denied</h2>
                        <p className="mt-1 text-sm text-slate-600">You do not have permission to view toll transactions.</p>
                    </CardContent>
                </Card>
            </DashboardShell>
        );
    }

    return (
        <DashboardShell>
            <div className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Ancillary billing</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Toll transactions</h1>
                    <p className="text-sm text-slate-500 mt-1">Individual toll rows extracted from bills. This is a drill-down surface — <Link to="/admin/tolls/bills" className="text-blue-900 hover:underline" data-testid="tolls-back-to-bills">go to Bills</Link> for the primary view.</p>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-right">
                        <p className="text-3xl font-display font-bold text-slate-900 tabular-nums" data-testid="tolls-total-count">{total}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Transactions</p>
                    </div>
                    <Button variant="outline" onClick={exportCsv} className="gap-1.5" data-testid="tolls-export-csv">
                        <Download size={14} /> Export CSV
                    </Button>
                    <Button asChild variant="outline" className="gap-1.5" data-testid="tolls-settings-link">
                        <Link to="/admin/tolls/settings"><Settings size={14} /> Settings</Link>
                    </Button>
                    {canInvoice && (
                        <Button
                            onClick={() => setGenOpen(true)}
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                            data-testid="tolls-generate-btn"
                        >
                            <Sparkles size={14} /> Generate draft invoices
                        </Button>
                    )}
                </div>
            </div>

            {/* Status chip filters */}
            <div className="mt-6 flex flex-wrap gap-2" data-testid="tolls-status-chips">
                {STATUS_FILTERS.map((opt) => {
                    const active = status === opt.value;
                    return (
                        <button
                            key={opt.value || "all"}
                            onClick={() => updateParam("status", opt.value)}
                            data-testid={`tolls-chip-${opt.value || "all"}`}
                            className={
                                "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors " +
                                (active
                                    ? "bg-blue-900 text-white"
                                    : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50")
                            }
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>

            {/* Filter card */}
            <Card className="mt-4">
                <CardContent className="p-4 flex flex-wrap gap-3 items-center">
                    <form onSubmit={onSearch} className="flex-1 min-w-[220px] flex gap-2">
                        <div className="relative flex-1">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search plate, location, authority, customer, unit…"
                                className="pl-9"
                                data-testid="tolls-search-input"
                            />
                        </div>
                        <Button type="submit" variant="outline" data-testid="tolls-search-go">Search</Button>
                    </form>
                    <div className="flex items-center gap-1.5 text-sm text-slate-500">
                        <span>From</span>
                        <Input type="date" value={dateFrom} onChange={(e) => updateParam("from", e.target.value)}
                               className="w-[160px]" data-testid="tolls-from-date" />
                        <span>to</span>
                        <Input type="date" value={dateTo} onChange={(e) => updateParam("to", e.target.value)}
                               className="w-[160px]" data-testid="tolls-to-date" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Filter size={14} className="text-slate-400" />
                        <select
                            value={disputeFilter}
                            onChange={(e) => updateParam("dispute", e.target.value)}
                            className="flex h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                            data-testid="tolls-dispute-filter"
                        >
                            {DISPUTE_FILTERS.map((o) => (
                                <option key={o.value || "all"} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            value={customerId}
                            onChange={(e) => updateParam("customer_id", e.target.value)}
                            placeholder='Customer — "MRSM Express" or ID'
                            className="w-[220px] text-xs"
                            data-testid="tolls-customer-filter"
                        />
                        <Input
                            value={rentalId}
                            onChange={(e) => updateParam("rental_id", e.target.value)}
                            placeholder='Rental — "TRK-1023" or ID'
                            className="w-[200px] text-xs"
                            data-testid="tolls-rental-filter"
                        />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <select
                            value={limit}
                            onChange={(e) => updateParam("limit", e.target.value)}
                            className="flex h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                            data-testid="tolls-page-size"
                        >
                            {LIMIT_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
                        </select>
                        <Button variant="ghost" size="sm" onClick={load} data-testid="tolls-refresh" title="Refresh">
                            <RefreshCw size={14} />
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="mt-4">
                <CardContent className="p-0">
                    {loading && <p className="text-sm text-slate-500 p-6" data-testid="tolls-loading">Loading…</p>}
                    {!loading && items.length === 0 && (
                        <div className="p-12 text-center" data-testid="tolls-empty">
                            <p className="text-sm text-slate-600">No toll transactions match your filters.</p>
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <table className="min-w-full text-sm">
                            <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                                <tr>
                                    <th className="text-left py-3 px-4">Posted</th>
                                    <th className="text-left py-3 px-4">Plate</th>
                                    <th className="text-left py-3 px-4">Location / Authority</th>
                                    <th className="text-right py-3 px-4">Amount</th>
                                    <th className="text-left py-3 px-4">Customer</th>
                                    <th className="text-left py-3 px-4">Rental</th>
                                    <th className="text-left py-3 px-4">Parent bill</th>
                                    <th className="text-left py-3 px-4">Status</th>
                                    <th className="text-right py-3 px-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((t) => {
                                    const st = resolveTollStatus(t);
                                    const custLbl = customerLabel(t.customer_id);
                                    const rentLbl = rentalLabel(t.rental_id);
                                    return (
                                        <tr key={t.transaction_id} data-testid={`tolls-row-${t.transaction_id}`} className="hover:bg-slate-50/50">
                                            <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{fmtDate(t.posted_at)}</td>
                                            <td className="py-3 px-4 font-mono text-slate-700">{t.plate || "—"}</td>
                                            <td className="py-3 px-4 max-w-[260px]">
                                                <div className="truncate text-slate-800" title={t.location || ""} data-testid={`tolls-location-${t.transaction_id}`}>{t.location || "—"}</div>
                                                {t.authority && (
                                                    <div className="truncate text-[11px] text-slate-500" title={t.authority} data-testid={`tolls-authority-${t.transaction_id}`}>
                                                        {t.authority}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-right font-semibold tabular-nums">{fmtMoney(t.amount_cents)}</td>
                                            <td className="py-3 px-4 max-w-[220px]">
                                                {t.customer_id ? (
                                                    <Link
                                                        to={`/admin/customers/${t.customer_id}`}
                                                        className="text-blue-900 hover:underline text-xs truncate block"
                                                        title={custLbl || ""}
                                                        data-testid={`tolls-customer-${t.transaction_id}`}
                                                    >
                                                        {custLbl}
                                                    </Link>
                                                ) : <span className="text-slate-400 text-xs">—</span>}
                                            </td>
                                            <td className="py-3 px-4 max-w-[160px]">
                                                {t.rental_id ? (
                                                    <Link
                                                        to={`/admin/rentals/${t.rental_id}`}
                                                        className="text-blue-900 hover:underline text-xs font-mono truncate block"
                                                        title={rentLbl || ""}
                                                        data-testid={`tolls-rental-${t.transaction_id}`}
                                                    >
                                                        {rentLbl}
                                                    </Link>
                                                ) : <span className="text-slate-400 text-xs">—</span>}
                                            </td>
                                            <td className="py-3 px-4 max-w-[140px]">
                                                {t.bill_id ? (
                                                    <button
                                                        onClick={() => { setBillDetailId(t.bill_id); setBillDetailOpen(true); }}
                                                        className="text-blue-900 hover:underline text-xs font-mono truncate block max-w-full"
                                                        title={t.bill_id}
                                                        data-testid={`tolls-bill-${t.transaction_id}`}
                                                    >
                                                        {t.bill_id}
                                                    </button>
                                                ) : <span className="text-slate-400 text-xs">—</span>}
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span
                                                        className={`inline-flex items-center rounded-full ring-1 px-2 py-0.5 text-[11px] font-semibold ${st.tone}`}
                                                        data-testid={`tolls-status-${t.transaction_id}`}
                                                    >
                                                        {st.label}
                                                    </span>
                                                    {t.has_attachment && (
                                                        <FileText size={12} className="text-slate-500" title="PDF attachment available" data-testid={`tolls-attach-${t.transaction_id}`} />
                                                    )}
                                                    {t.dispute_flag && st.code !== "disputed" && (
                                                        <AlertTriangle size={12} className="text-red-600" title="Dispute flag set" />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <div className="flex items-center gap-1 justify-end flex-wrap">
                                                    <Button
                                                        asChild={false}
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openDetail(t)}
                                                        data-testid={`tolls-open-${t.transaction_id}`}
                                                        className="gap-1"
                                                    >
                                                        View <ChevronRight size={14} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {!loading && total > limit && (
                <div className="mt-4 flex items-center justify-between text-sm text-slate-600" data-testid="tolls-pagination">
                    <span>Page {currentPage} of {totalPages} · {total} total</span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={skip <= 0} onClick={() => updateParam("skip", Math.max(0, skip - limit))} data-testid="tolls-page-prev">Previous</Button>
                        <Button variant="outline" size="sm" disabled={skip + limit >= total} onClick={() => updateParam("skip", skip + limit)} data-testid="tolls-page-next">Next</Button>
                    </div>
                </div>
            )}

            <TollTransactionDetailModal
                open={detailOpen}
                onOpenChange={setDetailOpen}
                txnId={detailTxnId}
                canManage={canManage}
                onChanged={load}
            />
            <TollBillDetailModal
                open={billDetailOpen}
                onOpenChange={setBillDetailOpen}
                billId={billDetailId}
                canManage={canManage}
                onChanged={load}
            />
            <TollGenerateInvoicesModal
                open={genOpen}
                onOpenChange={setGenOpen}
                onCreated={load}
            />
        </DashboardShell>
    );
}
